import type { TopicRecord } from "@/shared/domain/topics";
import { Goto } from "@/shared/navigation";
import type { WorkspaceActionPayload } from "@/shared/services/AiAgentThreadPubSubBus/models";

import { ApprovalMode } from "../approvalModes";
import type { ClientToolDeps, ClientToolExecutionResult } from "../ClientTools";
import { createClientTool, toolError, toolSuccess } from "../ClientTools";

import { SearchTopicConfirmation } from "./confirmations";
import {
  markNavigatedToVisualizer,
  MAX_MESSAGE_PATHS_PER_CALL,
  publishWhenVisualizerReady,
  VISUALIZER_NOT_READY_ERROR,
  visualizerStatus,
} from "./workspaceActions";

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

export interface SearchTopicInput {
  topic_id: string;
  /** Timestamp in nanoseconds since Unix epoch. */
  timestamp?: number;
}

// ---------------------------------------------------------------------------
// Result shape
// ---------------------------------------------------------------------------

/**
 * How many of the topic's message paths the result lists.
 *
 * The cap protects the thread's context window, not the wire. A tool result is
 * replayed to the model verbatim on every later turn of the thread
 * (`ClientToolBatchRunner`), so one search on a wide schema — PointCloud2,
 * Path, a camera's flattened header — would keep paying for a field list the
 * model read once. This is the same restraint `AiTopicDetail` applies at
 * `_FIELD_DETAIL_THRESHOLD` (`roboto_service/ai/views/topic.py`), which stops
 * inlining per-field records for exactly these topics.
 *
 * Pinned to the most message paths `open_panel` will accept in one call, so
 * the cap never drops a path the follow-up call could have used.
 */
const MAX_MESSAGE_PATHS_IN_RESULT = MAX_MESSAGE_PATHS_PER_CALL;

/**
 * The half of the result that says what did *not* happen, plus the ids that fix it.
 *
 * `success: true` next to `opened_in: "current_visualizer"` reads as "the data
 * is on screen", and the model acts on that reading: in production, asked to
 * open a front and a rear camera feed, it called this tool twice, got exactly
 * that payload back, and told the user both feeds were open while the
 * visualizer still showed the previous panel. Naming the topic's message paths
 * here turns the dead end into a one-hop recovery — `open_panel` on the next
 * turn, with no second round of lookups to find the ids.
 */
function describeSidebarFilter(topic: TopicRecord): Record<string, unknown> {
  const messagePaths = topic.message_paths;
  const listed = messagePaths.slice(0, MAX_MESSAGE_PATHS_IN_RESULT);
  const truncated = listed.length < messagePaths.length;

  const note =
    "No panel was created and no data is displayed — this only filtered the " +
    "visualizer sidebar. To display the data, call client_visualize_open_panel " +
    "with message_path_ids taken from message_paths below.";

  return {
    panel_created: false,
    note: truncated
      ? `${note} Listed the first ${listed.length} of ${messagePaths.length} ` +
        `message paths; call get_topic_schema for the rest.`
      : note,
    message_paths: listed.map((messagePath) => ({
      message_path_id: messagePath.message_path_id,
      message_path: messagePath.message_path,
      canonical_data_type: messagePath.canonical_data_type,
    })),
    ...(truncated ? { truncated: true } : {}),
  };
}

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

async function execute(
  input: SearchTopicInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  if (!deps?.goto) {
    return toolError("Navigation is not available");
  }
  if (!deps.topicService) {
    return toolError("Topic service is not available");
  }

  if (!input.topic_id) {
    return toolError("topic_id is required");
  }

  try {
    const topicService = deps.topicService;
    const topic = await topicService.getTopicById(input.topic_id);
    const timestamp =
      input.timestamp !== undefined ? BigInt(input.timestamp) : undefined;

    const payload: WorkspaceActionPayload = {
      type: "open_topic_search",
      payload: { topic, timestamp },
    };
    // Not `opened_in`: that is open_panel's success vocabulary, and reusing
    // it here is what let "success" read as "the data is on screen" for a
    // tool that displays nothing.
    const filteredInPlace = () =>
      toolSuccess("search_topic", {
        sidebar_filtered_in: "current_visualizer",
        ...describeSidebarFilter(topic),
      });

    const status = visualizerStatus(deps);

    if (status === "unreachable") {
      return toolError(VISUALIZER_NOT_READY_ERROR);
    }

    // "opening" means a batch-mate is already bringing a visualizer up.
    // Navigating again would replace the workspace it is carrying, so filter
    // that one's sidebar once it starts listening instead.
    if (status !== "closed") {
      // Falling through to the deep link on a missing dep would replace the
      // user's whole workspace to accomplish a sidebar filter; a wiring bug
      // must fail loudly instead.
      if (!deps.publishWorkspaceAction) {
        return toolError("Workspace actions are not available");
      }
      const published =
        status === "open"
          ? deps.publishWorkspaceAction(payload)
          : await publishWhenVisualizerReady(
              deps.publishWorkspaceAction,
              payload,
              deps.batchScope,
            );
      return published
        ? filteredInPlace()
        : toolError(VISUALIZER_NOT_READY_ERROR);
    }

    deps.goto.visualize({
      dataSource: { files: [topic.association.association_id] },
      params: { t: timestamp },
      state: {
        initialTopicFilter: {
          term: topic.topic_name,
          key: Date.now(),
        },
      },
    });

    markNavigatedToVisualizer(deps);

    return toolSuccess("search_topic", {
      navigated_to: Goto.route.Visualize,
      ...describeSidebarFilter(topic),
    });
  } catch (err) {
    return toolError(err instanceof Error ? err.message : String(err));
  }
}

// ---------------------------------------------------------------------------
// Tool module export
// ---------------------------------------------------------------------------

export const tool = createClientTool<SearchTopicInput>({
  name: "client_visualize_search_topic",
  description: `Filter the visualizer sidebar to one topic. Displays no data: no panel is created, no plot or image appears, and the visualizer keeps showing whatever it showed before. Use it only when the user wants to browse a topic's message paths before deciding what to open. To actually show data — a camera feed, a plot, raw messages — call client_visualize_open_panel with message path ids instead; this tool returns the topic's message paths (capped at ${MAX_MESSAGE_PATHS_IN_RESULT}) so that call needs no further lookup. If the user is not already on the visualizer page, navigates there first. Optionally seeks the timeline to a specific timestamp after opening.`,
  input_schema: {
    type: "object",
    properties: {
      topic_id: {
        type: "string",
        description:
          "Topic ID (e.g., 'tp_abc123'). The topic to search for in the sidebar.",
      },
      timestamp: {
        type: "integer",
        description:
          "Timestamp in nanoseconds since Unix epoch. Optional — seeks the timer after opening.",
      },
    },
    required: ["topic_id"],
    additionalProperties: false,
  },
  execute,
  defaultApprovalMode: ApprovalMode.AUTO_EXECUTE,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Search Topic",
  displayDescription: "Opens the visualizer with a topic search filter.",
  ConfirmationContent: SearchTopicConfirmation,
});
